/**
 * Local image paths for industries and subcategories.
 * Files live under `public/images/{industry-folder}/`.
 *
 * Routing map:
 *   Home → CategoryProductsPage (`/:industryId`) → CategoryDetailPage (`/:industryId/:slug`)
 */

const ROOT = "/images";

/** One hero + two alternates per main category (category landing pages). */
export const INDUSTRY_GALLERY = {
  legal: [
    `${ROOT}/legal-images/landprops.jpg`,
    `${ROOT}/legal-images/contract-law.jpg`,
    `${ROOT}/legal-images/marraige-law.jpg`,
  ],
  "real-estate": [
    `${ROOT}/real-estate-images/land-purchase.jpg`,
    `${ROOT}/real-estate-images/property-sale.jpg`,
    `${ROOT}/real-estate-images/apartment-search.jpg`,
  ],
  nutrition: [
    `${ROOT}/nutrition-images/clean-eating.jpg`,
    `${ROOT}/nutrition-images/weight-loss.jpg`,
    `${ROOT}/nutrition-images/local-recipe.jpg`,
  ],
  health: [
    `${ROOT}/health-images/medical-consult.jpg`,
    `${ROOT}/health-images/health-insurance.jpg`,
    `${ROOT}/health-images/preventive-health.jpg`,
  ],
  careers: [
    `${ROOT}/career-images/local-jobs.jpg`,
    `${ROOT}/career-images/international-jobs.jpg`,
    `${ROOT}/career-images/cv-writng.jpg`,
  ],
  "betting-gaming": [
    `${ROOT}/betting-images/bet-platform.jpg`,
    `${ROOT}/betting-images/tips.jpg`,
    `${ROOT}/betting-images/bonuses.jpg`,
  ],
};

/** Cover image per subcategory slug (topic hub + cards). */
export const SUBCATEGORY_IMAGES = {
  legal: {
    "land-property-law": `${ROOT}/legal-images/landprops.jpg`,
    "marriage-family-law": `${ROOT}/legal-images/marraige-law.jpg`,
    "business-corporate-law": `${ROOT}/legal-images/bus-law.jpg`,
    "criminal-law": `${ROOT}/legal-images/criminal-law.jpg`,
    "employment-labour-law": `${ROOT}/legal-images/labour-law.jpg`,
    "immigration-travel-law": `${ROOT}/legal-images/immigration-law.jpg`,
    "estate-planning-inheritance": `${ROOT}/legal-images/estate.jpg`,
    "debt-financial-legal": `${ROOT}/legal-images/debt-law.jpg`,
    "contract-law": `${ROOT}/legal-images/contract-law.jpg`,
    "personal-injury-claims": `${ROOT}/legal-images/accident-claims.jpg`,
  },
  "real-estate": {
    "land-purchase-verification": `${ROOT}/real-estate-images/land-purchase.jpg`,
    "property-sales": `${ROOT}/real-estate-images/property-sale.jpg`,
    "rental-apartment-search": `${ROOT}/real-estate-images/apartment-search.jpg`,
    "mortgage-home-financing": `${ROOT}/real-estate-images/mortgage.jpg`,
    "real-estate-investment": `${ROOT}/real-estate-images/investment.jpg`,
    "property-documentation": `${ROOT}/real-estate-images/property-document.jpg`,
    "land-disputes-legal": `${ROOT}/real-estate-images/land-dsipute.jpg`,
    "short-term-rentals": `${ROOT}/real-estate-images/airbnb.jpg`,
    "property-development": `${ROOT}/real-estate-images/property-construction.jpg`,
    "property-valuation": `${ROOT}/real-estate-images/property-valuation.jpg`,
  },
  nutrition: {
    "weight-loss-meal-plans": `${ROOT}/nutrition-images/weight-loss.jpg`,
    "weight-gain-muscle-building": `${ROOT}/nutrition-images/muscle-building.jpg`,
    "diabetic-meal-plans": `${ROOT}/nutrition-images/diabetic-meal.jpg`,
    "family-budget-meals": `${ROOT}/nutrition-images/family-meal.jpg`,
    "healthy-eating-professionals": `${ROOT}/nutrition-images/health-professional.jpg`,
    "african-recipes": `${ROOT}/nutrition-images/local-recipe.jpg`,
    "kids-school-meals": `${ROOT}/nutrition-images/kids-meal.jpg`,
    "vegetarian-vegan": `${ROOT}/nutrition-images/vegan-meals.jpg`,
    "detox-clean-eating": `${ROOT}/nutrition-images/clean-eating.jpg`,
    "fitness-athlete-nutrition": `${ROOT}/nutrition-images/fitness.jpg`,
  },
  health: {
    "health-insurance-plans": `${ROOT}/health-images/health-insurance.jpg`,
    "medical-consultations": `${ROOT}/health-images/medical-consult.jpg`,
    "sexual-reproductive-health": `${ROOT}/health-images/sexual-reproductive.jpg`,
    "mental-health-counseling": `${ROOT}/health-images/mental-health.jpg`,
    "chronic-disease-management": `${ROOT}/health-images/chronic-disease.jpg`,
    "weight-loss-programs": `${ROOT}/health-images/lifestyle-program.jpg`,
    "maternal-child-health": `${ROOT}/health-images/maternal-child.jpg`,
    "pharmacy-medication": `${ROOT}/health-images/pharmacy-access.jpg`,
    "diagnostic-lab-services": `${ROOT}/health-images/diagnostic-test.jpg`,
    "preventive-wellness": `${ROOT}/health-images/preventive-health.jpg`,
  },
  careers: {
    "local-jobs": `${ROOT}/career-images/local-jobs.jpg`,
    "international-jobs": `${ROOT}/career-images/international-jobs.jpg`,
    "cv-interview-prep": `${ROOT}/career-images/cv-writng.jpg`,
    "skill-training-certifications": `${ROOT}/career-images/certificate.jpg`,
    "remote-freelancing": `${ROOT}/career-images/freelancing.jpg`,
    "entry-level-jobs": `${ROOT}/career-images/entey-level.jpg`,
    "career-switching": `${ROOT}/career-images/career-consult.jpg`,
    "internship-graduate": `${ROOT}/career-images/career-program.jpg`,
    "entrepreneurship-startups": `${ROOT}/career-images/start-up.jpg`,
    "professional-development": `${ROOT}/career-images/prof-development.jpg`,
  },
  "betting-gaming": {
    "best-betting-platforms": `${ROOT}/betting-images/bet-platform.jpg`,
    "sports-betting-tips": `${ROOT}/betting-images/tips.jpg`,
    "casino-games": `${ROOT}/betting-images/casino-games.jpg`,
    "odds-comparison-strategy": `${ROOT}/betting-images/odds-comparison.jpg`,
    "bonuses-promotions": `${ROOT}/betting-images/bonuses.jpg`,
    "mobile-betting-apps": `${ROOT}/betting-images/mobile-app.jpg`,
    "responsible-gambling": `${ROOT}/betting-images/bet-responsibly.jpg`,
    "esports-betting": `${ROOT}/betting-images/esports-betting.jpg`,
    "virtual-games": `${ROOT}/betting-images/virtuals.jpg`,
    "payment-methods": `${ROOT}/betting-images/payment-methods.jpg`,
  },
};

export function getIndustryGalleryImages(industryId) {
  return INDUSTRY_GALLERY[industryId] ?? INDUSTRY_GALLERY["real-estate"];
}

export function getSubcategoryImage(industryId, slug) {
  if (!industryId || !slug) return null;
  return SUBCATEGORY_IMAGES[industryId]?.[slug] ?? null;
}

/** Replace remote URLs in rich mock payloads with bundled public images. */
export function patchRichDataImages(data, industryId, slug) {
  if (!data || typeof data !== "object") return data;

  const primary = getSubcategoryImage(industryId, slug);
  const gallery = getIndustryGalleryImages(industryId);
  const next = { ...data };

  if (primary) {
    next.heroImage = primary;
  }

  if (Array.isArray(next.galleryImages)) {
    next.galleryImages = next.galleryImages.map((_, index) =>
      index === 0 && primary ? primary : gallery[(index + 1) % gallery.length],
    );
  }

  return next;
}
